import { Button, TooltipWithButton, TypographyV2 } from '@which/seatbelt'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classNames from 'classnames'

import type { Option } from './QuestionScreen'
import styles from './QuestionScreen.module.scss'

type QuestionOptionProps = {
  option: Option
  optionIndex: number
  screenName: string
  isSelected: boolean
  isTooltipOpen: boolean
  onSelect: (optionIndex: number) => void
  onTooltipOpen: (optionIndex: number) => void
  onTooltipClose: () => void
}

export const QuestionOption = ({
  option,
  optionIndex,
  screenName,
  isSelected,
  isTooltipOpen,
  onSelect,
  onTooltipOpen,
  onTooltipClose,
}: QuestionOptionProps) => (
  <div className={styles.singleOptionContainer}>
    <Button
      label={option.label}
      onClick={() => onSelect(optionIndex)}
      appearance="secondary"
      className={classNames(styles.optionButton, {
        [styles.optionButtonSelected]: isSelected,
      })}
    >
      <TypographyV2
        tag="span"
        textStyle="sb-text-body-default-strong"
        className={classNames({
          [styles.optionButtonLabelWithTooltip]: !!option.tooltip,
        })}
      >
        {option.label}
      </TypographyV2>
      {option.tooltip && (
        <TypographyV2
          tag="span"
          textStyle="sb-text-interface-body-small-regular"
          className={styles.optionButtonTooltipSubtitle}
        >
          {option.tooltip}
        </TypographyV2>
      )}
    </Button>
    {option.tooltip && (
      // TooltipWithButton manages its own open/close state internally and does not
      // expose a controlled `isTooltipOpen` prop. To ensure only one tooltip is open
      // at a time, we use the `key` prop to force-remount any tooltip that is NOT the
      // currently open one (giving it a 'reset' key suffix), which resets its internal
      // state to closed. The active tooltip keeps a stable key so it is not remounted.
      // openHandler/closeHandler keep `openTooltipIndex` in sync with the internal state.
      <TooltipWithButton
        key={`tooltip-${optionIndex}-${!isTooltipOpen ? 'reset' : 'stable'}`}
        contents={option.tooltip}
        title={option.label}
        className={styles.tooltip}
        ariaLabel={option.label}
        isTooltipDisplayedAboveOverride={true}
        openHandler={() => {
          onTooltipOpen(optionIndex)
          dynamicGa4DataLayerPush({
            event: 'click_tooltip',
            item_index: optionIndex + 1,
            item_parent_text: screenName,
            item_text: option.label,
            item_group: 'personalisation tools',
            tool_name: 'Laptop picker helper',
            item_personalisation: 'false',
          })
        }}
        closeHandler={onTooltipClose}
      />
    )}
  </div>
)
